import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in Leaflet with bundlers
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface DeviceMapProps {
  latitude?: number;
  longitude?: number;
  deviceName: string;
}

const DeviceMap: React.FC<DeviceMapProps> = ({ latitude, longitude, deviceName }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!mapRef.current) return;

    // Initialize map
    const map = L.map(mapRef.current, {
      center: [latitude || 44.4647, longitude || 26.0601],
      zoom: 13,
      zoomControl: true,
      scrollWheelZoom: true,
    });

    // Add OpenStreetMap tiles
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 18,
    }).addTo(map);

    // Add device marker if coordinates are available
    if (latitude && longitude) {
      const marker = L.marker([latitude, longitude]).addTo(map);
      marker.bindPopup(`
        <div style="color: #333; font-family: Arial, sans-serif;">
          <strong>${deviceName}</strong><br/>
          Lat: ${latitude.toFixed(6)}°<br/>
          Lng: ${longitude.toFixed(6)}°
        </div>
      `);
    }

    mapInstanceRef.current = map;

    // Cleanup
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [latitude, longitude, deviceName]);

  if (!latitude || !longitude) {
    return (
      <div style={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#1a1a1a',
        color: '#888',
        fontSize: '14px',
        textAlign: 'center',
        padding: '20px'
      }}>
        <div>
          <div style={{ marginBottom: '8px' }}>📍 GPS coordinates not available</div>
          <div style={{ fontSize: '12px' }}>Map will display when device location is received</div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={mapRef}
      style={{
        height: '100%',
        width: '100%',
        borderRadius: '8px',
        overflow: 'hidden'
      }}
    />
  );
};

export default DeviceMap;
